import React, {
	type ChangeEvent,
	type FocusEvent,
	type KeyboardEvent,
	type MouseEvent,
	useCallback,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState
} from "react";

import type { ComboboxOption, ComboboxProps } from "./combobox.types";

import styles from "./Combobox.module.css";

const matches = (option: ComboboxOption, term: string): boolean =>
	option.label.toLowerCase().includes(term) || (option.description?.toLowerCase().includes(term) ?? false);

/**
 * A text input that filters a list as you type — the WAI-ARIA editable combobox with list
 * autocomplete. Focus never leaves the input; the highlighted option is announced through
 * `aria-activedescendant`, so the list itself never needs to take focus.
 *
 * Choosing is always explicit, by Enter or a click. Typing only narrows the list, and leaving the
 * field without choosing puts the committed value back, so a half-typed word can never quietly become
 * the value.
 *
 * Enter is always consumed here. These controls sit inside forms, and an Enter that reached the form
 * would submit it — saving a whole dialog because someone pressed Enter to pick a list item.
 */
export const Combobox = ({
	ariaLabel,
	disabled = false,
	emptyText,
	inputClassName,
	onChange,
	options,
	placeholder,
	value
}: ComboboxProps) => {
	const baseId = useId();
	const listboxId = `${baseId}-listbox`;
	const listRef = useRef<HTMLUListElement>(null);

	const committedLabel = useMemo(
		() => options.find((option: ComboboxOption) => option.value === value)?.label ?? "",
		[options, value]
	);

	const [inputValue, setInputValue] = useState(committedLabel);
	const [isOpen, setIsOpen] = useState(false);
	const [isFiltering, setIsFiltering] = useState(false);
	const [activeIndex, setActiveIndex] = useState(-1);

	// While the list is closed the input mirrors the committed value, which a parent can change on its
	// own — switching group, for one, picks a different relation.
	useEffect(() => {
		if (!isOpen) {
			setInputValue(committedLabel);
		}
	}, [committedLabel, isOpen]);

	const visibleOptions = useMemo(() => {
		const term = inputValue.trim().toLowerCase();

		return isFiltering && term ? options.filter((option: ComboboxOption) => matches(option, term)) : options;
	}, [inputValue, isFiltering, options]);

	useEffect(() => {
		if (!isOpen || activeIndex < 0) {
			return;
		}

		listRef.current
			?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`)
			?.scrollIntoView({ block: "nearest" });
	}, [activeIndex, isOpen]);

	const openList = useCallback((): void => {
		setIsOpen(true);
		// Start on the committed option, so the arrow keys move on from where the user already is.
		const committedIndex = options.findIndex((option: ComboboxOption) => option.value === value);
		setActiveIndex(Math.max(0, committedIndex));
	}, [options, value]);

	const closeList = useCallback((): void => {
		setIsOpen(false);
		setIsFiltering(false);
		setActiveIndex(-1);
		setInputValue(committedLabel);
	}, [committedLabel]);

	const choose = useCallback(
		(option: ComboboxOption): void => {
			setIsOpen(false);
			setIsFiltering(false);
			setActiveIndex(-1);
			setInputValue(option.label);

			if (option.value !== value) {
				onChange(option.value);
			}
		},
		[onChange, value]
	);

	const onInputFocus = useCallback((event: FocusEvent<HTMLInputElement>): void => {
		// Selected on arrival, so the first keystroke replaces the current label rather than appending to it.
		event.target.select();
	}, []);

	const onInputClick = useCallback((): void => {
		if (!isOpen) {
			openList();
		}
	}, [isOpen, openList]);

	const onInputChange = useCallback((event: ChangeEvent<HTMLInputElement>): void => {
		setInputValue(event.target.value);
		setIsFiltering(true);
		setIsOpen(true);
		setActiveIndex(0);
	}, []);

	const onInputBlur = useCallback((): void => {
		if (isOpen) {
			closeList();
		}
	}, [closeList, isOpen]);

	const onInputKeyDown = useCallback(
		(event: KeyboardEvent<HTMLInputElement>): void => {
			switch (event.key) {
				case "ArrowDown":
					event.preventDefault();
					if (isOpen) {
						setActiveIndex((current: number) => Math.min(current + 1, visibleOptions.length - 1));
					} else {
						openList();
					}
					break;
				case "ArrowUp":
					event.preventDefault();
					if (isOpen) {
						setActiveIndex((current: number) => Math.max(current - 1, 0));
					} else {
						openList();
					}
					break;
				case "Enter":
					event.preventDefault();
					if (isOpen && visibleOptions[activeIndex]) {
						choose(visibleOptions[activeIndex]);
					} else if (!isOpen) {
						openList();
					}
					break;
				case "Escape":
					if (isOpen) {
						event.preventDefault();
						closeList();
					}
					break;
				case "Tab":
					if (isOpen) {
						closeList();
					}
					break;
				default:
			}
		},
		[activeIndex, choose, closeList, isOpen, openList, visibleOptions]
	);

	const onOptionMouseDown = useCallback(
		(event: MouseEvent<HTMLLIElement>): void => {
			// mousedown rather than click: cancelling it keeps focus in the input, so the blur that closes
			// the list never gets in ahead of the choice.
			event.preventDefault();

			const option = visibleOptions[Number(event.currentTarget.dataset.index)];
			if (option) {
				choose(option);
			}
		},
		[choose, visibleOptions]
	);

	const activeOptionId = isOpen && visibleOptions[activeIndex] ? `${baseId}-option-${activeIndex}` : undefined;

	return (
		<div className={styles.combobox}>
			<input
				aria-activedescendant={activeOptionId}
				aria-autocomplete="list"
				aria-controls={listboxId}
				aria-expanded={isOpen}
				aria-label={ariaLabel}
				autoComplete="off"
				className={[styles.input, inputClassName].filter(Boolean).join(" ")}
				disabled={disabled}
				onBlur={onInputBlur}
				onChange={onInputChange}
				onClick={onInputClick}
				onFocus={onInputFocus}
				onKeyDown={onInputKeyDown}
				placeholder={placeholder}
				role="combobox"
				spellCheck={false}
				type="text"
				value={inputValue}
			/>

			{isOpen && (
				<ul ref={listRef} aria-label={ariaLabel} className={styles.listbox} id={listboxId} role="listbox">
					{visibleOptions.length === 0 ? (
						<li className={styles.empty} role="presentation">
							{emptyText}
						</li>
					) : (
						visibleOptions.map((option: ComboboxOption, index: number) => (
							<li
								key={option.value}
								aria-selected={index === activeIndex}
								className={index === activeIndex ? styles.activeOption : styles.option}
								data-index={index}
								id={`${baseId}-option-${index}`}
								onMouseDown={onOptionMouseDown}
								role="option"
								tabIndex={-1}>
								<span className={styles.label}>{option.label}</span>
								{option.description && <span className={styles.description}>{option.description}</span>}
							</li>
						))
					)}
				</ul>
			)}
		</div>
	);
};
